import { useState } from "react";
import { Text } from "src/common/Text";
import { Dialog } from "@/v2-components/ui/simple-dialog/Dialog";
import { Button } from "@/v2-components/ui/button";
import { CONSENT_CATEGORIES, ConsentCategory } from "./constants";
import { ConsentState, useConsentStore } from "./consentStore";
import { useTrans } from "@/utils/useTrans";

function CategoryToggle({
  name,
  description,
  required,
  checked,
  onChange,
}: {
  name: string;
  description: string;
  required: boolean;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  const { t } = useTrans(["gnb"]);
  return (
    <div className="flex flex-col gap-y-3">
      <div className="flex items-start gap-x-2">
        <input
          type="checkbox"
          checked={checked}
          disabled={required}
          onChange={(e) => onChange(e.target.checked)}
          className={required ? "pointer-events-none opacity-50" : ""}
        />
        <div className="flex flex-col gap-y-1">
          <Text
            as="span"
            className="text-222222 whitespace-pre-wrap text-xs font-700"
            text={t(name)}
          />
          <Text
            as="span"
            className="text-222222 whitespace-pre-wrap text-xs font-400"
            text={t(description)}
          />
        </div>
      </div>
      <div className="bg-gray_3 h-px w-full" />
    </div>
  );
}

export function ConsentSettings({
  onClose,
  onSaved,
}: {
  onClose: () => void;
  onSaved: () => void;
}) {
  const consent = useConsentStore((s) => s.consent);
  const acceptAll = useConsentStore((s) => s.acceptAll);
  const acceptNecessaryOnly = useConsentStore((s) => s.acceptNecessaryOnly);
  const savePreferences = useConsentStore((s) => s.savePreferences);
  const showDeclineButton = useConsentStore((s) => s.showDeclineButton);
  const { t } = useTrans(["gnb"]);

  const [localConsent, setLocalConsent] = useState<ConsentState>({
    ...consent,
  });

  const handleToggle = (category: ConsentCategory, checked: boolean) => {
    setLocalConsent((prev) => ({ ...prev, [category]: checked }));
  };

  return (
    <Dialog
      maxWidth="xl"
      position="center"
      className="flex flex-col overflow-hidden bg-white px-6 py-6 md:px-8 md:py-10"
      title={t("Cookie Settings")}
      onClose={onClose}
    >
      <div className="flex max-h-[80dvh] grow flex-col gap-y-6 md:gap-y-10">
        <div className="mt-3 grow overflow-auto md:mt-10">
          <div className="flex flex-col gap-y-6 md:gap-y-10">
            <Text
              as="span"
              className="text-222222 whitespace-pre-wrap text-sm font-400"
              text={t(
                "We use cookies to deliver and maintain bHaptics services and protect against spam, fraud, and abuse, measure audience engagement and site statistics to understand how our services are used and improve the quality of those services.\n\nSome of the cookies are necessary and they are there to technically enable the website and its components as well as to enable security measures. The necessary cookies are activated without user consent because they are necessary for the website to work properly and in a secure way. All other cookies and tracking technologies are not classified as necessary and are subject to consent from website visitors.",
              )}
            />
            <div className="flex flex-col gap-y-3">
              <Text
                as="span"
                className="text-222222 whitespace-pre-line font-700"
                text={t("Cookies")}
              />
              <div className="flex flex-col gap-y-5">
                {CONSENT_CATEGORIES.map((category) => (
                  <CategoryToggle
                    key={category.id}
                    name={category.name}
                    description={category.description}
                    required={category.required}
                    checked={localConsent[category.id]}
                    onChange={(checked) => handleToggle(category.id, checked)}
                  />
                ))}
              </div>
            </div>
            <div className="flex flex-col gap-y-3">
              <Text
                as="span"
                className="text-222222 whitespace-pre-line font-700"
                text={t("Managing Cookies")}
              />
              <Text
                as="span"
                className="text-222222 whitespace-pre-wrap text-sm font-400"
                text={t(
                  "You can manage your cookie preferences through the settings in your web browser. You can choose to accept or reject cookies, or selectively disable certain types of cookies. However, please note that disabling cookies may affect your site experience and the functionality of certain features.",
                )}
              />
            </div>
            <div className="flex flex-col gap-y-3">
              <Text
                as="span"
                className="text-222222 whitespace-pre-line font-700"
                text={t("Contact Us")}
              />
              <div>
                <Text
                  as="span"
                  className="text-222222 whitespace-pre-wrap text-sm font-400"
                  text={t(
                    "If you have any questions about our use of cookies, ",
                  )}
                />
                <a
                  href="mailto:infosec@bhaptics.com"
                  target="_blank"
                  rel="noreferrer"
                >
                  <Text
                    as="span"
                    className="text-blue_1 cursor-pointer whitespace-pre-wrap text-sm font-400"
                    text="infosec@bhaptics.com"
                  />
                </a>
              </div>
            </div>
          </div>
        </div>
        <span className="block h-px w-full bg-coolgray-90 bg-opacity-10" />
        <div className="flex w-full flex-wrap justify-end gap-2 md:gap-3">
          <Button
            onClick={() => {
              savePreferences(localConsent);
              onSaved();
            }}
            size="sm"
            variant="darkgray-outline"
            className="!px-6"
          >
            {t("Save My Preferences")}
          </Button>
          {showDeclineButton && (
            <Button
              onClick={() => {
                acceptNecessaryOnly();
                onSaved();
              }}
              size="sm"
              variant="darkgray-outline"
              className="!px-6"
            >
              {t("Accept Only Necessary")}
            </Button>
          )}
          <Button
            onClick={() => {
              acceptAll();
              onSaved();
            }}
            size="sm"
            variant="blue-fill"
            className="!px-6"
          >
            {t("Accept All")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
